<article #scrollContainer>
  <!-- Coaching Script Section -->
  <section>
    <div class="header-block plan-block">
      <h2 [attr.id]="'coaching-plan'">Coaching Script</h2>
      <div *ngIf="coachingExamples.length" class="link" (click)="openModalExamples()">
        <i class="fa-solid fa-circle-info icon"></i>Examples
      </div>
      <div *ngIf="coachingStatistics.length" class="link" (click)="openModalStatistics()">
        <i class="fa-solid fa-chart-simple icon"></i>Statistics
      </div>
    </div>
    <div class="editor-container" *ngIf="canEditInterventions()">
      <app-editor
        [hideFileUpload]="true"
        [hideImageUpload]="true"
        [(contents)]="coachingScriptContents"
        (contentsChange)="onEditorContentChange($event)"
      ></app-editor>
      <button
        *ngIf="showSaveButton"
        mat-flat-button
        color="primary"
        (click)="saveCoachingScript()"
      >
        Save
      </button>
      <button *ngIf="showSaveButton" mat-stroked-button (click)="cancelCoachingScript()">
        Cancel
      </button>
    </div>
    <!-- Read-only view for users without edit permissions -->
    <div class="editor-container" *ngIf="!canEditInterventions()">
      <app-editor
        [hideFileUpload]="true"
        [hideImageUpload]="true"
        [contents]="coachingScriptContents"
        [disabled]="true"
      ></app-editor>
    </div>
    <div *ngIf="isLoading && !canEditInterventions()" class="spinner-container">
      <mat-progress-spinner
        mode="indeterminate"
        [diameter]="30"
        class="spinner"
      ></mat-progress-spinner>
    </div>
  </section>

  <!-- Linked Events Section -->
  <section>
    <div class="header-block">
      <h2 [attr.id]="'linked-events'">Linked Events</h2>
    </div>
    <div class="table-container">
      <table mat-table matSort [dataSource]="linkedEventList" class="linked-events-table">
        <ng-container matColumnDef="type">
          <th mat-header-cell *matHeaderCellDef>Type</th>
          <td mat-cell *matCellDef="let element" (click)="onRowClick(element)">
            {{ element?.eventType }}
          </td>
        </ng-container>

        <ng-container matColumnDef="criteria">
          <th mat-header-cell *matHeaderCellDef>Criteria</th>
          <td
            class="criteria-column"
            mat-cell
            *matCellDef="let element"
            (click)="onRowClick(element)"
            matTooltip="{{ addSpaceAfterComma(element?.criteria) }}"
          >
            {{ addSpaceAfterComma(element?.criteria) }}
          </td>
        </ng-container>

        <ng-container matColumnDef="severity">
          <th mat-header-cell *matHeaderCellDef>Severity</th>
          <td mat-cell *matCellDef="let element" (click)="onRowClick(element)">
            <i
              *ngIf="element.severity === 'Critical'"
              [matTooltip]="element.severity"
              class="fa-solid fa-angle-double-up icon-red icon-default-size"
            ></i>
            <i
              *ngIf="element.severity === 'Significant'"
              [matTooltip]="element.severity"
              class="fa-solid fa-chevron-up icon-red icon-default-size"
            ></i>
            <i
              *ngIf="element.severity === 'Moderate'"
              [matTooltip]="element.severity"
              class="fa-solid fa-equals icon-orange icon-default-size"
            ></i>
            <i
              *ngIf="element.severity === 'Minor'"
              [matTooltip]="element.severity"
              class="fa-solid fa-chevron-down icon-gray icon-default-size"
            ></i>
          </td>
        </ng-container>

        <ng-container matColumnDef="date">
          <th mat-header-cell *matHeaderCellDef>Date</th>
          <td mat-cell *matCellDef="let element" (click)="onRowClick(element)">
            {{ element?.processDate | utcDate }}
          </td>
        </ng-container>

        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let element" (click)="onRowClick(element)">
            <ng-container [ngSwitch]="element?.status">
              <ng-container *ngFor="let status of statuses">
                <span *ngSwitchCase="status.viewValue">
                  <i [class]="status.icon"></i>
                  <span class="status-text">{{ status.viewValue }}</span>
                </span>
              </ng-container>
            </ng-container>
          </td>
        </ng-container>

        <ng-container matColumnDef="menu">
          <th mat-header-cell *matHeaderCellDef>Menu</th>
          <td mat-cell *matCellDef="let row">
            <div class="icon-container" [matMenuTriggerFor]="menu">
              <i class="fas fa-ellipsis"></i>
            </div>
            <mat-menu #menu="matMenu">
              <button mat-menu-item (click)="onUnlinkIntervention(row)">Unlink</button>
            </mat-menu>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="linkedEventColumns"></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: linkedEventColumns"
          [class.row-selected]="isRowSelected(row.id)"
          class="clickable"
        ></tr>
      </table>
    </div>

    <div *ngIf="isLoading" class="spinner-container">
      <mat-progress-spinner
        mode="indeterminate"
        [diameter]="30"
        class="spinner"
      ></mat-progress-spinner>
    </div>

    <div *ngIf="!isLoading && linkedEventList?.length === 0" class="no-records-found">
      <p>No records found.</p>
    </div>
  </section>

  <!-- Notes Section -->
  <section *ngIf="canViewInterventionNotes()" [attr.id]="'notes-coaching'">
    <app-notes
      [avatarUrl]="avatarUrl"
      [userName]="userName"
      [contents]="noteContents"
      [notes]="notes"
      [entityId]="dataRow.id"
      [sectionId]="'notes'"
      [entityType]="entityType"
      [assigneeId]="userId"
      [showSaveAndCompleteButton]="shouldShowSaveAndCompleteButton()"
      (saveAndCompleteClicked)="onSaveAndCompleteClicked()"
    ></app-notes>
  </section>

  <!-- Audit History Section -->
  <section [attr.id]="'audit-history'">
    <div class="header-block">
      <h2>History</h2>
    </div>

    <div *ngIf="isLoadingAudit" class="spinner-container">
      <mat-progress-spinner
        mode="indeterminate"
        [diameter]="30"
        class="spinner"
      ></mat-progress-spinner>
    </div>

    <div
      *ngIf="!isLoadingAudit && interventionAuditHistory?.length === 0"
      class="no-records-found"
    >
      <p>No history records found.</p>
    </div>

    <div *ngIf="!isLoadingAudit && interventionAuditHistory?.length">
      <div class="history-container">
        <div class="history-item" *ngFor="let item of displayedAuditHistory">
          <div class="history-content">
            <!-- Desktop view -->
            <div class="history-user-info desktop-view">
              <div class="history-avatar">
                <ng-container
                  *ngIf="item.changedBy !== 'System Generated'; else systemAvatar"
                >
                  <img
                    *ngIf="assigneeImages[item.changedById]"
                    [src]="assigneeImages[item.changedById]"
                    appImgFallback
                    alt="Avatar"
                    class="user-avatar"
                  />
                  <div
                    *ngIf="!assigneeImages[item.changedById]"
                    class="initials"
                    [ngClass]="getBackgroundClass(item.changedBy)"
                    matTooltip="{{ item.changedBy }}"
                  >
                    {{ getInitials(item.changedBy) }}
                  </div>
                </ng-container>
                <ng-template #systemAvatar>
                  <img
                    src="assets/images/brocade-workflow.svg"
                    class="avatar-icon"
                    alt="System Avatar"
                    matTooltip="System Generated"
                  />
                </ng-template>
              </div>
              <span class="history-user">{{ item.changedBy }}</span>
              <ng-container
                *ngIf="
                  item.changeType === 'Created' && !item.fieldChanged;
                  else regularUpdate
                "
              >
                created the
              </ng-container>
              <ng-template #regularUpdate> updated the </ng-template>
              <span class="history-field">{{
                formatFieldName(item.fieldChanged) || item.changeType
              }}</span>
              <span class="history-date">
                {{ formatDateWithAt(item.dateChanged) }}
              </span>
            </div>

            <!-- Mobile view -->
            <div class="mobile-content-wrapper mobile-view">
              <div class="history-avatar">
                <ng-container
                  *ngIf="item.changedBy !== 'System Generated'; else systemAvatar"
                >
                  <img
                    *ngIf="assigneeImages[item.changedById]"
                    [src]="assigneeImages[item.changedById]"
                    appImgFallback
                    alt="Avatar"
                    class="user-avatar"
                  />
                  <div
                    *ngIf="!assigneeImages[item.changedById]"
                    class="initials"
                    [ngClass]="getBackgroundClass(item.changedBy)"
                    matTooltip="{{ item.changedBy }}"
                  >
                    {{ getInitials(item.changedBy) }}
                  </div>
                </ng-container>
                <ng-template #systemAvatar>
                  <img
                    src="assets/images/brocade-workflow.svg"
                    class="avatar-icon"
                    alt="System Avatar"
                    matTooltip="System Generated"
                  />
                </ng-template>
              </div>
              <div class="mobile-text-content">
                <div class="history-user-info">
                  <span class="history-user">{{ item.changedBy }}</span>
                  <ng-container
                    *ngIf="
                      item.changeType === 'Created' && !item.fieldChanged;
                      else regularUpdate
                    "
                  >
                    created the
                  </ng-container>
                  <ng-template #regularUpdate> updated the </ng-template>
                </div>
                <div class="mobile-field-date">
                  <span class="history-field">{{
                    formatFieldName(item.fieldChanged) || item.changeType
                  }}</span>
                  <span class="history-date">
                    {{ formatDateWithAt(item.dateChanged) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="history-values" *ngIf="item.oldValue || item.newValue">
              <div class="history-old-value">
                <ng-container *ngIf="item.oldValue; else noOldValue">
                  <!-- Coaching Script field -->
                  <ng-container
                    *ngIf="item.fieldChanged === 'coaching_script'; else oldValueCheck"
                  >
                    <div [innerHTML]="item.oldValue"></div>
                  </ng-container>
                  <ng-template #oldValueCheck>
                    <!-- User fields (assignee, last_modified_by, created_by) -->
                    <ng-container
                      *ngIf="
                        item.fieldChanged === 'assignee' ||
                          item.fieldChanged === 'last_modified_by' ||
                          item.fieldChanged === 'created_by';
                        else organizationOldValueCheck
                      "
                    >
                      <div class="assignee-value">
                        <div class="history-avatar">
                          <ng-container
                            *ngIf="getAssigneeById(item.oldValue) as assignee"
                          >
                            <img
                              *ngIf="assigneeImages[item.oldValue]"
                              [src]="assigneeImages[item.oldValue]"
                              appImgFallback
                              alt="Avatar"
                              class="user-avatar"
                            />
                            <div
                              *ngIf="!assigneeImages[item.oldValue]"
                              class="initials"
                              [ngClass]="getBackgroundClass(getUserName(item.oldValue))"
                              matTooltip="{{ getUserName(item.oldValue) }}"
                            >
                              {{ getInitials(getUserName(item.oldValue)) }}
                            </div>
                          </ng-container>
                        </div>
                        {{ getUserName(item.oldValue) }}
                      </div>
                    </ng-container>
                    <ng-template #organizationOldValueCheck>
                      <!-- Organization field -->
                      <ng-container
                        *ngIf="
                          item.fieldChanged === 'organization_id';
                          else priorityOldValueCheck
                        "
                      >
                        {{ getOrganizationName(item.oldValue) }}
                      </ng-container>
                      <ng-template #priorityOldValueCheck>
                        <!-- Priority field -->
                        <ng-container
                          *ngIf="item.fieldChanged === 'priority'; else oldValueAsText"
                        >
                          <div class="priority-value">
                            <div class="select-trigger-content">
                              <div
                                class="safety-indicator {{
                                  item.oldValue ? item.oldValue.toLowerCase() : ''
                                }}"
                              >
                                <i class="mark mark-first"></i>
                                <i class="mark"></i>
                                <i class="mark"></i>
                              </div>
                            </div>
                            {{ item.oldValue }}
                          </div>
                        </ng-container>
                        <ng-template #oldValueAsText>
                          <!-- Due Date field -->
                          <ng-container
                            *ngIf="
                              item.fieldChanged === 'due_date';
                              else statusOldValueCheck
                            "
                          >
                            {{ convertFromDatabaseToDatePickerFormat(item.oldValue) }}
                          </ng-container>
                          <ng-template #statusOldValueCheck>
                            <!-- Status field -->
                            <ng-container
                              *ngIf="item.fieldChanged === 'status'; else regularOldValue"
                            >
                              <i [class]="getStatusIcon(item.oldValue)"></i>
                              {{ item.oldValue }}
                            </ng-container>
                            <!-- Regular text field -->
                            <ng-template #regularOldValue>
                              {{ item.oldValue }}
                            </ng-template>
                          </ng-template>
                        </ng-template>
                      </ng-template>
                    </ng-template>
                  </ng-template>
                </ng-container>
                <ng-template #noOldValue> None </ng-template>
              </div>
              <div class="history-arrow" *ngIf="item.newValue">
                <i class="fas fa-arrow-right"></i>
              </div>
              <div class="history-new-value" *ngIf="item.newValue">
                <!-- Coaching Script field -->
                <ng-container
                  *ngIf="item.fieldChanged === 'coaching_script'; else newValueCheck"
                >
                  <div [innerHTML]="item.newValue"></div>
                </ng-container>
                <ng-template #newValueCheck>
                  <!-- User fields (assignee, last_modified_by, created_by) -->
                  <ng-container
                    *ngIf="
                      item.fieldChanged === 'assignee' ||
                        item.fieldChanged === 'last_modified_by' ||
                        item.fieldChanged === 'created_by';
                      else organizationNewValueCheck
                    "
                  >
                    <div class="assignee-value">
                      <div class="history-avatar">
                        <ng-container *ngIf="getAssigneeById(item.newValue) as assignee">
                          <img
                            *ngIf="assigneeImages[item.newValue]"
                            [src]="assigneeImages[item.newValue]"
                            appImgFallback
                            alt="Avatar"
                            class="user-avatar"
                          />
                          <div
                            *ngIf="!assigneeImages[item.newValue]"
                            class="initials"
                            [ngClass]="getBackgroundClass(getUserName(item.newValue))"
                            matTooltip="{{ getUserName(item.newValue) }}"
                          >
                            {{ getInitials(getUserName(item.newValue)) }}
                          </div>
                        </ng-container>
                      </div>
                      {{ getUserName(item.newValue) }}
                    </div>
                  </ng-container>
                  <ng-template #organizationNewValueCheck>
                    <!-- Organization field -->
                    <ng-container
                      *ngIf="
                        item.fieldChanged === 'organization_id';
                        else priorityNewValueCheck
                      "
                    >
                      {{ getOrganizationName(item.newValue) }}
                    </ng-container>
                    <ng-template #priorityNewValueCheck>
                      <!-- Priority field -->
                      <ng-container
                        *ngIf="item.fieldChanged === 'priority'; else newValueAsText"
                      >
                        <div class="priority-value">
                          <div class="select-trigger-content">
                            <div
                              class="safety-indicator {{
                                item.newValue ? item.newValue.toLowerCase() : ''
                              }}"
                            >
                              <i class="mark mark-first"></i>
                              <i class="mark"></i>
                              <i class="mark"></i>
                            </div>
                          </div>
                          {{ item.newValue }}
                        </div>
                      </ng-container>
                      <ng-template #newValueAsText>
                        <!-- Due Date field -->
                        <ng-container
                          *ngIf="
                            item.fieldChanged === 'due_date';
                            else statusNewValueCheck
                          "
                        >
                          {{ convertFromDatabaseToDatePickerFormat(item.newValue) }}
                        </ng-container>
                        <ng-template #statusNewValueCheck>
                          <!-- Status field -->
                          <ng-container
                            *ngIf="item.fieldChanged === 'status'; else regularNewValue"
                          >
                            <i [class]="getStatusIcon(item.newValue)"></i>
                            {{ item.newValue }}
                          </ng-container>
                          <!-- Regular text field -->
                          <ng-template #regularNewValue>
                            {{ item.newValue }}
                          </ng-template>
                        </ng-template>
                      </ng-template>
                    </ng-template>
                  </ng-template>
                </ng-template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="audit-view-toggle"
        *ngIf="interventionAuditHistory.length > maxAuditHistory"
        (click)="toggleAuditHistoryView()"
      >
        <span>{{ showAllHistory ? "View Less" : "View All" }}</span>
        <i class="fas {{ showAllHistory ? 'fa-chevron-up' : 'fa-chevron-down' }}"></i>
      </div>
    </div>
  </section>
</article>

<ul *ngIf="sections.length > 1 && showMenu">
  <ng-container *ngFor="let item of sections; first as isFirst">
    <li (click)="onNavClick(item.id)" [class.active]="item.id === sectionSelected">
      <span>{{ item?.title }}</span>
      <i class="indicator"></i>
    </li>
  </ng-container>
</ul>
